import { useCallback, useEffect, useState } from 'react';

import { toggleThemeMode, useThemeMode, type ThemeMode } from './themeMode';

/**
 * The chrome's own state: is the sidebar a rail, is the mobile drawer open, is the page light or
 * dark.
 *
 * The template keeps this in Redux and mirrors it onto the document from a `LayoutSync` component,
 * because its stylesheet is driven by attributes and body classes rather than by props —
 * `body.mini-sidebar`, `html[data-theme]`. The mirroring is not optional; the CSS has no other
 * input. What *is* optional is Redux: three booleans shared by two components do not need a store,
 * an action catalogue and a provider, so this is a hook and the writes to the document happen here.
 *
 * The sidebar state is deliberately not persisted. It is a per-sitting choice, and restoring it
 * would mean a shop that once collapsed the menu never sees it expanded again. Theme is the
 * opposite and lives in {@link themeMode}, which owns it because `ConfigProvider` — far outside
 * this shell — needs it too.
 */

export interface LayoutState {
  /** Collapsed to an icon rail. Ignored below the `lg` breakpoint, where the drawer takes over. */
  miniSidebar: boolean;
  toggleMiniSidebar: () => void;

  /** Expanded temporarily because the pointer is over the rail. */
  expandMenu: boolean;
  setExpandMenu: (expanded: boolean) => void;

  /** The mobile drawer. A separate axis from `miniSidebar` — small screens have no rail. */
  menuOpened: boolean;
  openMobileMenu: () => void;
  closeMobileMenu: () => void;

  theme: ThemeMode;
  toggleTheme: () => void;
}

export function useLayoutState(): LayoutState {
  const [miniSidebar, setMiniSidebar] = useState(false);
  const [expandMenu, setExpandMenu] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const theme = useThemeMode();

  // Mirrored onto the document rather than passed as props: the template's stylesheet selects on
  // these, and there is no class name to hand a child instead.
  useEffect(() => {
    document.body.classList.toggle('mini-sidebar', miniSidebar);
  }, [miniSidebar]);

  useEffect(() => {
    document.body.classList.toggle('expand-menu', expandMenu);
  }, [expandMenu]);

  useEffect(() => {
    document.documentElement.classList.toggle('menu-opened', menuOpened);
  }, [menuOpened]);

  // The drawer is a small-screen affordance, and nothing else closes it when the window grows.
  // Without this a resize past `lg` leaves the overlay pinned over a layout that no longer has a
  // drawer to dismiss — the page is unusable and the only visible cause is the window width.
  useEffect(() => {
    if (!menuOpened) {
      return;
    }

    const wide = window.matchMedia('(min-width: 1024px)');
    const close = () => {
      if (wide.matches) {
        setMenuOpened(false);
      }
    };

    wide.addEventListener('change', close);

    return () => wide.removeEventListener('change', close);
  }, [menuOpened]);

  return {
    miniSidebar,
    toggleMiniSidebar: useCallback(() => setMiniSidebar((value) => !value), []),
    expandMenu,
    setExpandMenu,
    menuOpened,
    openMobileMenu: useCallback(() => setMenuOpened(true), []),
    closeMobileMenu: useCallback(() => setMenuOpened(false), []),
    theme,
    toggleTheme: toggleThemeMode,
  };
}
